'use client';

import { css } from '@bts-web/utils-style-engine';
import {
  useClientTranslation,
  interpolateTranslation,
} from '@bts-web/utils-lokalise';
import { TFunction } from 'i18next';
import {
  intlFiatValueFormatting,
  MaximumFractionDigits,
} from '@bts-web/utils-formatting';
import { Drawer } from '../../../common/components/Drawer/Drawer';
import { HTMLRenderer } from '../../../common/components/HTMLRenderer/HTMLRenderer';
import { useDrawerMetadata } from '../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { ButtonBase } from '../../../common/components/ButtonBase/ButtonBase';
import {
  getTradeAssetSelectorTranslations,
  IconMapper,
} from '../../ChangeCurrencyOrSwap/changeCurrencyUtils';

interface FiatWalletDetailsDrawerProps {
  currency: string;
  locale: string;
  walletAmount: string;
}

export const FiatWalletDetailsDrawer = ({
  currency,
  locale,
  walletAmount,
}: FiatWalletDetailsDrawerProps) => {
  const { t } = useClientTranslation();

  const { closeThisDrawer } = useDrawerMetadata();

  const rawTranslations = getTradeAssetSelectorTranslations(t as TFunction);

  const formattedAmount = intlFiatValueFormatting(Number(walletAmount), {
    locale,
    currency,
    fractionDigits: MaximumFractionDigits.FIAT,
  });

  const translations = {
    ...rawTranslations,
    fiatWalletLabel: interpolateTranslation(rawTranslations.fiatWalletLabel, {
      fiatsymbol: currency,
    }),
    walletBalanceTitle: interpolateTranslation(
      rawTranslations.walletBalanceTitle,
      { fiatsymbol: currency },
    ),
    walletBalanceDescription: interpolateTranslation(
      rawTranslations.walletBalanceDescription,
      { amount: formattedAmount, fiatsymbol: currency },
    ),
  };

  return (
    <Drawer title={translations.fiatWalletLabel}>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'small',
            px: 'medium',
            pt: 'medium',
            flex: 1,
          })}
        >
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: 'extra_small',
              textAlign: 'center',
            })}
          >
            <h3
              className={css({
                fontSize: 'headline.medium_medium',
                fontWeight: 'headline.medium_medium',
                lineHeight: 'headline.medium_medium',
                letterSpacing: 'headline.medium_medium',
                color: 'neutrals.text_primary',
              })}
            >
              {translations.walletBalanceTitle}
            </h3>

            <p
              className={css({
                fontSize: 'body.medium',
                fontWeight: 'body.medium',
                lineHeight: 'body.medium',
                letterSpacing: 'body.medium',
                color: 'neutrals.text_secondary',
              })}
            >
              <HTMLRenderer>
                {translations.walletBalanceDescription}
              </HTMLRenderer>
            </p>
          </div>

          <div
            className={css({
              display: 'flex',
              gap: 'medium',
              alignItems: 'center',
              py: 'medium',
            })}
          >
            {IconMapper[currency as keyof typeof IconMapper]}

            <span
              className={css({
                flex: 1,
                fontSize: 'label.medium_medium',
                fontWeight: '500',
                lineHeight: 'label.medium_medium',
                letterSpacing: 'label.medium_medium',
                color: 'neutrals.text_primary',
              })}
            >
              {translations.fiatWalletLabel}
            </span>

            <div
              className={css({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 'extra_small_3',
              })}
            >
              <span
                className={css({
                  fontSize: 'caption.small',
                  fontWeight: 'caption.small',
                  lineHeight: 'caption.small',
                  letterSpacing: 'caption.small',
                  color: 'neutrals.text_secondary',
                })}
              >
                {translations.available}
              </span>
              <span
                className={css({
                  fontSize: 'label.medium_medium',
                  fontWeight: '500',
                  lineHeight: 'label.medium_medium',
                  letterSpacing: 'label.medium_medium',
                  color: 'neutrals.text_primary',
                })}
              >
                {formattedAmount}
              </span>
            </div>
          </div>
        </div>

        <div
          className={css({
            px: 'medium',
            paddingTop: 'small',
            paddingBottom: 'medium',
          })}
        >
          <ButtonBase
            visual="accent"
            size="large"
            fullWidth
            onClick={closeThisDrawer}
          >
            {translations.close}
          </ButtonBase>
        </div>
      </div>
    </Drawer>
  );
};
